<script setup lang="ts">
    import { InputRow, Select, SectionHeader, MultiCombobox, Combobox } from '@/components/ui/form';
    import { Input } from '@/components/ui/input';
    import {
        optionsCompanyServices,
        optionsYesNo,
        optionsFrequency,
        optionsWeekdays,
    } from '@/utils/form';
    import type { SelectOption, User } from '@/types';
    import Permission from '@/enums/Permission';
    import { usePermissions } from '@/composables/usePermissions';

    const { hasAnyPermission } = usePermissions();

    withDefaults(
        defineProps<{
            form: any;
            keepersEmployees: User[];
            clients?: User[];
            kubeOptions: SelectOption[];
        }>(),
        {
            clients: () => [],
        },
    );
</script>

<template>
    <div class="space-y-5">
        <SectionHeader>{{ $t('companies.services') }}</SectionHeader>
        <div class="grid gap-3 sm:grid-cols-1">
            <InputRow
                :form="form"
                name="services"
                label="companies.form.choose_services"
            >
                <MultiCombobox
                    v-model="form.services"
                    :options="optionsCompanyServices"
                    :translate="true"
                    :placeholder="$t('companies.form.choose_services')"
                />
            </InputRow>
        </div>
        <SectionHeader>{{ $t('companies.general_settings') }}</SectionHeader>
        <div class="grid gap-3 sm:grid-cols-2">
            <InputRow
                :form="form"
                name="num_of_vouchers"
                label="companies.form.system_details.num_of_vouchers"
            >
                <Input
                    id="num_of_vouchers"
                    v-model="form.num_of_vouchers"
                    autocomplete="num_of_vouchers"
                    :placeholder="$t('companies.form.system_details.num_of_vouchers')"
                    type="number"
                />
            </InputRow>
            <InputRow
                :form="form"
                name="has_payments"
                label="companies.form.system_details.has_payments"
            >
                <Select
                    v-model="form.has_payments"
                    :options="optionsYesNo"
                    :translate="true"
                />
            </InputRow>
            <div class="flex gap-2">
                <InputRow
                    :form="form"
                    name="fixed_price"
                    label="companies.form.system_details.fixed_price"
                    class="flex-1"
                >
                    <Input
                        id="fixed_price"
                        v-model="form.fixed_price"
                        autocomplete="fixed_price"
                        :placeholder="$t('companies.form.system_details.fixed_price')"
                        type="number"
                    />
                </InputRow>
                <InputRow
                    :form="form"
                    name="price_frequency"
                    label="companies.form.system_details.price_frequency"
                >
                    <Select
                        v-model="form.price_frequency"
                        :options="[
                            { value: 'hourly', label: 'companies.price_frequency_options.hourly' },
                            {
                                value: 'monthly',
                                label: 'companies.price_frequency_options.monthly',
                            },
                        ]"
                        :translate="true"
                    />
                </InputRow>
            </div>
            <InputRow
                :form="form"
                name="voucher_limit"
                label="companies.form.system_details.voucher_limit"
            >
                <Input
                    id="voucher_limit"
                    v-model="form.voucher_limit"
                    autocomplete="voucher_limit"
                    :placeholder="$t('companies.form.system_details.voucher_limit')"
                    type="number"
                />
            </InputRow>
            <InputRow
                :form="form"
                name="report_deadline"
                label="companies.form.system_details.report_deadline"
            >
                <Input
                    id="report_deadline"
                    v-model="form.report_deadline"
                    autocomplete="report_deadline"
                    :placeholder="$t('companies.form.system_details.report_deadline')"
                    type="number"
                />
            </InputRow>
            <InputRow
                :form="form"
                name="salary_deadline"
                label="companies.form.system_details.salary_deadline"
            >
                <Input
                    id="salary_deadline"
                    v-model="form.salary_deadline"
                    autocomplete="salary_deadline"
                    :placeholder="$t('companies.form.system_details.salary_deadline')"
                    type="number"
                />
            </InputRow>
            <InputRow
                :form="form"
                name="vat_frequency"
                label="companies.form.system_details.vat_frequency"
            >
                <Select
                    v-model="form.vat_frequency"
                    :options="optionsFrequency"
                    :translate="true"
                />
            </InputRow>
            <InputRow
                :form="form"
                name="update_frequency"
                label="companies.form.system_details.update_frequency"
            >
                <Select
                    v-model="form.update_frequency"
                    :options="optionsFrequency.filter((option) => option.value !== 'n/a')"
                    :translate="true"
                />
            </InputRow>
            <InputRow
                :form="form"
                name="has_e_income"
                label="companies.form.system_details.has_e_income"
            >
                <Select
                    v-model="form.has_e_income"
                    :options="optionsYesNo"
                    :translate="true"
                />
            </InputRow>
            <InputRow
                :form="form"
                name="day_frequency"
                label="companies.form.system_details.day_frequency"
            >
                <MultiCombobox
                    v-model="form.day_frequency"
                    :options="optionsWeekdays"
                    :translate="true"
                    :placeholder="$t('companies.form.system_details.day_frequency')"
                />
            </InputRow>
            <InputRow
                :form="form"
                name="salary_system"
                label="companies.form.system_details.salary_system"
            >
                <Input
                    id="salary_system"
                    v-model="form.salary_system"
                    autocomplete="salary_system"
                    :placeholder="$t('companies.form.system_details.salary_system')"
                />
            </InputRow>
            <InputRow
                v-if="
                    hasAnyPermission([
                        Permission.SALARY_PAYSLIPS_VIEW,
                        Permission.SALARY_PAYROLLS_VIEW,
                    ])
                "
                :form="form"
                name="salary_two_step_approval"
                label="companies.two_step_verification"
            >
                <Select
                    v-model="form.salary_two_step_approval"
                    :options="optionsYesNo"
                    :translate="true"
                />
            </InputRow>
            <InputRow
                v-if="form.salary_two_step_approval"
                :form="form"
                name="approver_1"
                label="companies.approver_1"
            >
                <Combobox
                    v-model="form.approver_1"
                    :options="clients"
                    :placeholder="$t('companies.approver_1')"
                    :loading="!keepersEmployees.length"
                    label-by="name"
                    value-by="id"
                />
            </InputRow>
            <InputRow
                v-if="form.salary_two_step_approval"
                :form="form"
                name="approver_2"
                label="companies.approver_2"
            >
                <Combobox
                    v-model="form.approver_2"
                    :options="clients"
                    :placeholder="$t('companies.approver_2')"
                    :loading="!keepersEmployees.length"
                    label-by="name"
                    value-by="id"
                />
            </InputRow>
        </div>

        <SectionHeader>{{ $t('companies.kube_settings') }}</SectionHeader>

        <div class="grid gap-3 sm:grid-cols-2">
            <InputRow
                :form="form"
                name="has_kube"
                label="companies.form.system_details.has_kube"
            >
                <Select
                    v-model="form.has_kube"
                    :options="optionsYesNo"
                    :translate="true"
                />
            </InputRow>
            <InputRow
                :form="form"
                name="kube_id"
                label="companies.form.system_details.kube_id"
            >
                <Input
                    id="kube_id"
                    v-model="form.kube_id"
                    autocomplete="kube_id"
                    :placeholder="$t('companies.form.system_details.kube_id')"
                    disabled
                />
            </InputRow>
            <InputRow
                :form="form"
                name="to_keepers_kube"
                label="companies.form.system_details.to_keepers_kube"
            >
                <Select
                    v-model="form.to_keepers_kube"
                    :options="optionsYesNo"
                    :translate="true"
                />
            </InputRow>
            <InputRow
                :form="form"
                name="kube_data_field_id"
                label="companies.form.system_details.kube_data_field_id"
            >
                <Select
                    v-model="form.kube_data_field_id"
                    :options="kubeOptions"
                    value-by="id"
                    label-by="name"
                />
            </InputRow>
        </div>
    </div>
</template>
